var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { Easing, Tween } from "@tweenjs/tween.js";
import { Player_Type } from "@/types/PlayerData";
import Title, { MAX_EXP } from "./Title";
import Weapon from "./Weapon";
import metadata from "@assets/images/metadata.json";
import config from "@/config";
import Handlers from "@/lib/Handlers";
import { NotificationType } from "@/types/Notification";
import UtilsScreen from "@/lib/utils/screen";
var DEFAULT_SPEED = 15;
var ATTACK_DEFAULT_SPEED = 25;
var MAX_LEVEL = metadata.characters.length - 1;
var OFFSET = {
    title: function (level) { return ({
        x: 0,
        y: -450 + level * -1,
    }); },
};
var Player = /** @class */ (function (_super) {
    __extends(Player, _super);
    function Player(scene, data) {
        var _this = _super.call(this, scene, data.position.x, data.position.y) || this;
        _this.data_player = data;
        _this.invisible = false;
        var game = _this.data_player.game;
        // init visuals
        _this.character = new Phaser.Physics.Arcade.Sprite(_this.scene, 0, 0, "char_" + game.level);
        _this.weapon = new Weapon(_this.scene, _this.data_player);
        _this.title = new Title(_this.scene, OFFSET.title(game.level).x, OFFSET.title(game.level).y, _this.data_player);
        // init anims
        _this.scene.anims.create({
            key: "death",
            frameRate: 18,
            frames: _this.character.anims.generateFrameNumbers("deathAnimation", {
                start: 0,
                end: 17,
            }),
        });
        // init character
        _this.scene.physics.world.enable(_this.character);
        _this.character.setImmovable(false);
        _this.character.setCollideWorldBounds(false);
        _this.setScale(0.2);
        _this.setDepth(_this.data_player.type === Player_Type.PLAYER ? 100 : 98);
        _this.title.setDepth(_this.depth - 1);
        _this.add(_this.character);
        _this.add(_this.weapon);
        _this.add(_this.title);
        _this.scene.add.existing(_this);
        // init handlers
        _this.initHandlers();
        // invisible
        _this.setInvisible(true);
        // resize
        var _a = UtilsScreen.safe_area(_this.scene), x_min = _a.x_min, x_max = _a.x_max, y_min = _a.y_min, y_max = _a.y_max;
        var _width = x_max - x_min;
        var _height = y_max - y_min;
        _this.resize(_width, _height);
        return _this;
    }
    Player.prototype.NOTIFY_DEATH = function () {
        if (this.getInvisible())
            return;
        Handlers.NotificationHandler.getInstance().push({
            type: NotificationType.DEATH,
            detail: {
                id: this.data_player.id,
                is_alive: false,
            },
        });
    };
    Player.prototype.NOTIFY_ALIVE = function () {
        if (this.data_player.game.is_alive)
            return;
        Handlers.NotificationHandler.getInstance().push({
            type: NotificationType.DEATH,
            detail: {
                id: this.data_player.id,
                is_alive: true,
            },
        });
    };
    Player.prototype.setInvisible = function (value) {
        var _this = this;
        var _a, _b, _c;
        if (this.data_player.type === Player_Type.ENEMY)
            return;
        if (!value) {
            this.invisible = false;
            (_a = this.tween_invisible) === null || _a === void 0 ? void 0 : _a.stop();
            (_b = this.timer_invisible) === null || _b === void 0 ? void 0 : _b.destroy();
            this.setAlpha(1);
            return;
        }
        // data
        this.invisible = true;
        // animation
        var v = { alpha: 1 };
        this.tween_invisible = new Tween(v)
            .to({ alpha: 0.5 }, 500)
            .easing(Easing.Cubic.InOut)
            .repeat(8)
            .yoyo(true)
            .onUpdate(function (_a) {
            var alpha = _a.alpha;
            _this.setAlpha(alpha);
        })
            .onComplete(function () {
            _this.setAlpha(1);
        })
            .start();
        // timer
        (_c = this.timer_invisible) === null || _c === void 0 ? void 0 : _c.destroy();
        this.timer_invisible = this.scene.time.addEvent({
            callback: function () {
                _this.setInvisible(false);
            },
            delay: 2500,
            loop: false,
        });
    };
    Player.prototype.initHandlers = function () {
        var _this = this;
        var inputHandler = Handlers.InputHandler.getInstance();
        var notificationHandler = Handlers.NotificationHandler.getInstance();
        var gameHandler = Handlers.GameHandler.getInstance();
        // input
        if (this.data_player.type === 1)
            return;
        notificationHandler.on(NotificationType.EXP, function (detail) {
            if (!_this.scene)
                return;
            var exp = detail.exp;
            _this.addExp(exp);
        });
        if (!this.scene.sys.game.device.os.desktop)
            return;
        inputHandler.on("pointermove", function (data) {
            if (!_this.scene)
                return;
            if (gameHandler.isPaused())
                return;
            var mouseX = data.x;
            var mouseY = data.y;
            var angle = (Phaser.Math.Angle.Between(mouseX, mouseY, _this.scene.camera.width / 2, _this.scene.camera.height / 2) *
                180) /
                Math.PI;
            _this.rotate(Phaser.Math.DegToRad(angle - 270));
        });
        inputHandler.on("pointerdown", function () {
            if (!_this.scene)
                return;
            _this.attack();
        });
    };
    Player.prototype.updateLevel = function () {
        var level = this.data_player.game.level;
        var lvl = Math.min(level, MAX_LEVEL);
        this.character.setTexture("char_" + lvl);
        this.weapon.setLevel(lvl);
        this.title.setLevel(level);
        this.setScale(0.2 + level / 250);
    };
    Player.prototype.updateExp = function () {
        var _a = this.data_player.game, exp = _a.exp, level = _a.level;
        var max_exp = MAX_EXP(level);
        var newExp = exp < max_exp ? exp : exp - max_exp;
        var newLevel = exp < max_exp ? level : level + 1;
        this.data_player.game.exp = newExp;
        this.data_player.game.level = newLevel;
        this.title.setExp(exp);
        this.updateLevel();
    };
    Player.prototype.updateAlive = function () {
        this.setVisible(this.data_player.game.is_alive);
        if (this.data_player.game.is_alive) {
            this.setInvisible(true);
            return;
        }
        var animSprite = this.scene.add.sprite(this.x, this.y, "deathAnimation");
        animSprite.on("animationcomplete", function () {
            animSprite.destroy();
        });
        !animSprite.anims.isPlaying && animSprite.play("death");
    };
    Player.prototype.canMove = function () {
        return this.data_player.game.is_alive && !this.weapon.getAnimationStatus();
    };
    Player.prototype.getUser = function () {
        return {
            id: this.data_player.id,
            username: this.data_player.username,
        };
    };
    Player.prototype.getInvisible = function () {
        return this.invisible;
    };
    Player.prototype.destroy = function () {
        var _a, _b, _c, _d;
        (_a = this.timer_invisible) === null || _a === void 0 ? void 0 : _a.remove();
        (_b = this.character) === null || _b === void 0 ? void 0 : _b.destroy();
        (_c = this.weapon) === null || _c === void 0 ? void 0 : _c.destroy();
        (_d = this.title) === null || _d === void 0 ? void 0 : _d.destroy();
        _super.prototype.destroy.call(this);
    };
    Player.prototype.getTextureLevel = function (next) {
        return Math.min(this.data_player.game.level + (next ? 1 : 0), MAX_LEVEL);
    };
    Player.prototype.setAlpha = function (value) {
        var _a, _b, _c;
        _super.prototype.setAlpha.call(this, value);
        (_a = this.character) === null || _a === void 0 ? void 0 : _a.setAlpha(value);
        (_b = this.weapon) === null || _b === void 0 ? void 0 : _b.setAlpha(value);
        (_c = this.title) === null || _c === void 0 ? void 0 : _c.setAlpha(value);
        return this;
    };
    Player.prototype.setPosition = function (_x, _y) {
        var x = Math.max(Math.min(_x, config.world.dimension.boundary.width - 16), 32);
        var y = Math.max(Math.min(_y, config.world.dimension.boundary.height - 16), 32);
        _super.prototype.setPosition.call(this, x, y);
    };
    Player.prototype.setVisible = function (value) {
        _super.prototype.setVisible.call(this, value);
        this.character.setVisible(value);
        this.weapon.setVisible(value);
        this.title.setVisible(value);
    };
    Player.prototype.move = function () {
        if (!this.canMove())
            return;
        var game = this.data_player.game;
        var rotation = this.rotation + Math.PI / 2;
        var speed = (this.data_player.type ? 10 : DEFAULT_SPEED) *
            Player.DEFAULT_SCALE_FACTOR;
        var x = this.x + Math.cos(rotation) * (speed + game.level * 0.1);
        var y = this.y + Math.sin(rotation) * (speed + game.level * 0.1);
        this.setPosition(x, y);
    };
    Player.prototype.rotate = function (radians) {
        if (!this.canMove())
            return;
        this.setRotation(radians);
    };
    Player.prototype.attack = function () {
        var _this = this;
        if (Handlers.GameHandler.getInstance().isPaused())
            return;
        if (!this.canMove())
            return;
        this.setInvisible(false);
        this.weapon.attack(function () {
            var _a;
            (_a = _this.timer_anim) === null || _a === void 0 ? void 0 : _a.remove();
            _this.weapon.attackAnimation();
            _this.timer_anim = _this.scene.time.addEvent({
                delay: ATTACK_DEFAULT_SPEED,
                callback: function () { return _this.weapon.attackAnimation(); },
                repeat: 18,
            });
            if (_this.data_player.type === Player_Type.ENEMY)
                return;
            Handlers.AudioHandler.getInstance().play("player.attack");
        });
    };
    Player.prototype.getValue = function () {
        return this.data_player;
    };
    Player.prototype.getScore = function () {
        return this.data_player.game.score;
    };
    Player.prototype.addExp = function (exp) {
        this.data_player.game.exp += exp;
        this.updateExp();
        this.data_player.game.score += exp;
    };
    Player.prototype.addKillStrike = function () {
        this.data_player.game.count_kill += 1;
        this.data_player.game.count_strike += 1;
        this.data_player.game.score += 1000;
    };
    Player.prototype.death = function (callback) {
        var _this = this;
        var _a;
        this.data_player.game.is_alive = false;
        this.updateAlive();
        this.data_player.game.exp = 0;
        this.data_player.game.level = 0;
        this.data_player.game.count_strike = 0;
        this.updateExp();
        (_a = this.timer_death) === null || _a === void 0 ? void 0 : _a.destroy();
        this.timer_death = this.scene.time.addEvent({
            callback: function () {
                callback === null || callback === void 0 ? void 0 : callback();
                _this.revive();
            },
            delay: config.gameplay.player.death.ms,
            loop: false,
        });
        // play
        var audio_index = 0;
        if (this.data_player.type === Player_Type.PLAYER) {
            audio_index = Math.round(Math.random());
        }
        else {
            audio_index = 2;
        }
        Handlers.AudioHandler.getInstance().play("player.death.".concat(audio_index));
    };
    Player.prototype.revive = function () {
        var _a;
        (_a = this.timer_death) === null || _a === void 0 ? void 0 : _a.destroy();
        this.data_player.game.is_alive = true;
        this.updateAlive();
    };
    Player.prototype.resize = function (_width, _height) {
        var w_max = this.character.width;
        var h_max = this.character.height;
        var w = Math.min(_width * 0.8, w_max);
        var h = Math.min(_height * 0.8, h_max);
        var s_x = w / w_max;
        var s_y = h / h_max;
        var s = Math.min(s_x, s_y);
        Player.DEFAULT_SCALE_FACTOR = s;
        this.updateLevel();
    };
    Player.prototype.setScale = function (x, y) {
        var s_y = y !== null && y !== void 0 ? y : x;
        _super.prototype.setScale.call(this, x * Player.DEFAULT_SCALE_FACTOR, s_y * Player.DEFAULT_SCALE_FACTOR);
        return this;
    };
    Player.DEFAULT_SCALE_FACTOR = 1;
    return Player;
}(Phaser.GameObjects.Container));
export default Player;
